/**
 * PatientFollowup.jsx
 * src/pages/assistant/PatientFollowup.jsx
 *
 * Shows all Completed appointments as follow-up candidates.
 * Assistant can book a new follow-up appointment for any patient.
 */

import { useState, useEffect, useCallback } from "react";
import {
  BSModal, Avatar, PageHeader, Loading, EmptyState,
  MiniStat, apiFetch
} from "../../components/helpers";

const TODAY = new Date().toISOString().split("T")[0];

const FOLLOWUP_TYPES = [
  "Post-Treatment Check",
  "Root Canal Follow-up",
  "Braces Adjustment",
  "Teeth Cleaning",
  "X-Ray Review",
  "Pain Assessment",
  "Medicine Review",
  "General Check-up",
  "Other",
];

const EMPTY_BOOK = {
  patient_id: "",
  doctor_id: "",
  appointment_date: "",
  appointment_time: "10:00",
  treatment: "",
  notes: "",
};

function DaysBadge({ date }) {
  const diff = Math.ceil((new Date(date) - new Date(TODAY)) / 86400000);
  const daysAgo = -diff;
  if (daysAgo <= 0) return <span style={{ fontSize: 10, color: "#1D9E75", fontWeight: 700 }}>Today</span>;
  const label = daysAgo === 1 ? "1 day ago" : `${daysAgo} days ago`;
  const color = daysAgo > 30 ? "#E24B4A" : daysAgo > 14 ? "#F6A623" : "#6B7A8D";
  return <span style={{ fontSize: 10, color, fontWeight: 700 }}>{label}</span>;
}

export default function PatientFollowup() {
  const [items,      setItems]      = useState([]);
  const [doctors,    setDoctors]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterDays, setFilterDays] = useState("All");
  const [modalOpen,  setModalOpen]  = useState(false);
  const [form,       setForm]       = useState(EMPTY_BOOK);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [flash,      setFlash]      = useState({ msg: "", type: "success" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fuRes, docRes] = await Promise.all([
        apiFetch("get_followup_patients.php"),
        apiFetch("get_all_doctors.php"),
      ]);
      setItems(fuRes.data || []);
      setDoctors(docRes.data || []);
    } catch {
      setItems([]); setDoctors([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showFlash = (msg, type = "success") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash({ msg: "", type: "success" }), 3500);
  };

  const openBook = (item) => {
    setForm({
      ...EMPTY_BOOK,
      patient_id: item.patient_id,
      doctor_id: item.doctor_id,
      appointment_date: "",
      treatment: item.notes || "",
    });
    setError("");
    setModalOpen(true);
  };

  const handleBook = async () => {
    if (!form.patient_id)        return setError("Patient is required");
    if (!form.doctor_id)         return setError("Doctor is required");
    if (!form.appointment_date)  return setError("Date is required");
    if (!form.appointment_time)  return setError("Time is required");

    setSubmitting(true); setError("");
    try {
      const res = await apiFetch("book_appointment.php", {
        method: "POST",
        body: {
          patient_id: form.patient_id,
          doctor_id: form.doctor_id,
          appointment_date: form.appointment_date,
          appointment_time: form.appointment_time,
          treatment: form.treatment,
          notes: form.notes,
          status: "Pending",
        },
      });
      if (res.status) {
        setModalOpen(false);
        showFlash("Follow-up appointment booked successfully!");
        load();
      } else {
        setError(res.message || "Booking failed");
      }
    } catch { setError("Network error. Please retry."); }
    setSubmitting(false);
  };

  // Compute days since appointment for each item
  const enriched = items.map(i => ({
    ...i,
    daysAgo: Math.ceil((new Date(TODAY) - new Date(i.appointment_date)) / 86400000),
    needsFollowup: !Number(i.followup_booked),
  }));

  // Filter by days range
  const filtered = enriched.filter(f => {
    const matchSearch =
      f.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
      f.doctor_name?.toLowerCase().includes(search.toLowerCase()) ||
      f.notes?.toLowerCase().includes(search.toLowerCase());

    const matchDays =
      filterDays === "All" ? true :
      filterDays === "Overdue 30+" ? f.daysAgo >= 30 :
      filterDays === "2 weeks" ? (f.daysAgo >= 14 && f.daysAgo < 30) :
      filterDays === "This week" ? f.daysAgo < 7 :
      true;

    return matchSearch && matchDays;
  });

  // Stats
  const needsFollowup = enriched.filter(i => i.needsFollowup).length;
  const overdue30     = enriched.filter(i => i.daysAgo >= 30 && i.needsFollowup).length;
  const pending14     = enriched.filter(i => i.daysAgo >= 14 && i.daysAgo < 30 && i.needsFollowup).length;
  const booked        = enriched.filter(i => !i.needsFollowup).length;

  const inp = "form-control";

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>

      <PageHeader title="Follow-up Manager" subtitle="Track completed visits and book follow-up appointments">
        <select className="form-select" value={filterDays} onChange={e => setFilterDays(e.target.value)} style={{ width: 160, fontSize: 13 }}>
          <option value="All">All Patients</option>
          <option value="Overdue 30+">Overdue 30+ days</option>
          <option value="2 weeks">14–30 days ago</option>
          <option value="This week">Within 7 days</option>
        </select>
        <input className="form-control" placeholder="Search patient, doctor..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 210, fontSize: 13 }} />
        <button className="btn btn-light fw-semibold" onClick={load} style={{ fontSize: 13 }}>↻ Refresh</button>
      </PageHeader>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><MiniStat label="Total Completed" value={items.length}   accent="#D85A30" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Needs Follow-up" value={needsFollowup} accent="#378ADD" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Overdue (30+ days)" value={overdue30}  accent="#E24B4A" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Follow-up Booked" value={booked}       accent="#1D9E75" /></div>
      </div>

      {/* Flash */}
      {flash.msg && (
        <div className={`alert alert-${flash.type === "danger" ? "danger" : "success"} py-2 px-3 mb-3`} style={{ fontSize: 13 }}>
          {flash.msg}
        </div>
      )}

      {/* Alert for overdue patients */}
      {overdue30 > 0 && !loading && (
        <div className="alert d-flex align-items-center gap-2 mb-4 py-2 px-3"
          style={{ background: "#FFF3CD", border: "1px solid #FFD900", borderRadius: 12, fontSize: 13 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span><strong>{overdue30} patient{overdue30 > 1 ? "s" : ""}</strong> have not returned in 30+ days. Consider sending a reminder!</span>
        </div>
      )}

      {/* Table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? <Loading /> : filtered.length === 0 ? (
            <EmptyState message="No completed appointments found for follow-up." />
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr>
                    {["Patient", "Doctor", "Last Visit", "Since", "Treatment", "Follow-up", "Action"].map(h => (
                      <th key={h} className="text-muted fw-bold border-0 px-4 py-3"
                        style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f) => (
                    <tr key={f.appointment_id}
                      style={{ background: f.daysAgo >= 30 && f.needsFollowup ? "#FFFBF0" : "transparent" }}>

                      {/* Patient */}
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-2">
                          <Avatar name={f.patient_name} size={30} />
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>{f.patient_name || "—"}</div>
                            {f.mobile && <div style={{ fontSize: 10, color: "#6B7A8D" }}>📱 {f.mobile}</div>}
                          </div>
                        </div>
                      </td>

                      {/* Doctor */}
                      <td className="px-4" style={{ fontSize: 13, color: "#D85A30", fontWeight: 600 }}>
                        {f.doctor_name || "—"}
                        {f.specialization && <div style={{ fontSize: 10, color: "#6B7A8D", fontWeight: 400 }}>{f.specialization}</div>}
                      </td>

                      {/* Last visit date */}
                      <td className="px-4 fw-semibold" style={{ fontSize: 12, color: "#0A1628" }}>
                        {f.appointment_date}
                      </td>

                      {/* Days since */}
                      <td className="px-4">
                        <DaysBadge date={f.appointment_date} />
                      </td>

                      {/* Treatment notes */}
                      <td className="px-4 text-muted" style={{ fontSize: 12, maxWidth: 160 }}>
                        <span title={f.notes}>{f.notes ? (f.notes.length > 40 ? f.notes.slice(0, 40) + "…" : f.notes) : "—"}</span>
                      </td>

                      {/* Follow-up status */}
                      <td className="px-4">
                        {f.needsFollowup ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#E24B4A", background: "#FCEBEB",
                            padding: "3px 10px", borderRadius: 20 }}>⚠ Not Booked</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#085041", background: "#E1F5EE",
                            padding: "3px 10px", borderRadius: 20 }}>✓ Booked</span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-4">
                        <div className="d-flex gap-2 align-items-center">
                          <button
                            className="btn btn-sm fw-bold text-white"
                            style={{ background: "#D85A30", fontSize: 11, borderRadius: 8, whiteSpace: "nowrap" }}
                            onClick={() => openBook(f)}
                          >
                            📅 Book Follow-up
                          </button>
                          {f.mobile && (
                            <a
                              href={`https://wa.me/${f.mobile}?text=${encodeURIComponent(`Hello ${f.patient_name}, this is Om Dental Clinic. Your last visit was on ${f.appointment_date}. Please schedule your follow-up appointment soon.`)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm fw-bold text-white"
                              style={{ background: "#25D366", fontSize: 11, borderRadius: 8 }}
                              title="Send WhatsApp reminder"
                            >
                              📱 WA
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Book Follow-up Modal */}
      <BSModal open={modalOpen} title="Book Follow-up Appointment" size="modal-lg" onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>PATIENT *</label>
            <select className="form-select" value={form.patient_id}
              onChange={e => setForm({ ...form, patient_id: e.target.value })}>
              <option value="">Select Patient</option>
              {/* Show all patients from the enriched list */}
              {[...new Map(items.map(i => [i.patient_id, i])).values()].map(p => (
                <option key={p.patient_id} value={p.patient_id}>{p.patient_name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DOCTOR *</label>
            <select className="form-select" value={form.doctor_id}
              onChange={e => setForm({ ...form, doctor_id: e.target.value })}>
              <option value="">Select Doctor</option>
              {doctors.map(d => (
                <option key={d.doctor_id} value={d.doctor_id}>{d.name} — {d.specialization}</option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>FOLLOW-UP DATE *</label>
            <input type="date" className={inp} value={form.appointment_date} min={TODAY}
              onChange={e => setForm({ ...form, appointment_date: e.target.value })} />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>TIME *</label>
            <input type="time" className={inp} value={form.appointment_time}
              onChange={e => setForm({ ...form, appointment_time: e.target.value })} />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>TYPE / TREATMENT</label>
            <select className="form-select" value={form.treatment}
              onChange={e => setForm({ ...form, treatment: e.target.value })}>
              <option value="">Select Type</option>
              {FOLLOWUP_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="col-12">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>NOTES</label>
            <textarea className={inp} rows={2} value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Instructions for next visit..." />
          </div>
        </div>

        {error && <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>{error}</div>}

        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn btn-light fw-semibold" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30" }}
            onClick={handleBook} disabled={submitting}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Booking...</> : "📅 Book Follow-up"}
          </button>
        </div>
      </BSModal>
    </div>
  );
}